(function () {
  if (customElements.get("upgrade-set-selector")) return;

  // Mobile gets the inline add-to-cart selector, desktop gets links to each set's
  // own PDP — the two winning mechanisms from the 2026 bundle-upsell test, each on
  // the device where it won. 750px is the theme's own mobile/desktop boundary (the
  // @media in upgrade-set-selector.css), so layout and mechanism switch together.
  // This cannot be decided in Liquid: PDP HTML is CDN-cached per URL, not per
  // device, so a server-side choice would be served to the wrong devices.
  var DESKTOP_QUERY = "(min-width: 750px)";

  // When the host cannot constrain the set's size, the module picks one for the
  // shopper. Queen and King carry the bedroom-set business, in that order; any
  // other unconstrained option keeps first-available (see preferVariant).
  var SIZE_OPTION = "size";
  var SIZE_PREFERENCE = [/queen/i, /king/i];

  customElements.define(
    "upgrade-set-selector",
    class UpgradeSetSelector extends HTMLElement {
      disconnectedCallback() {
        this.unsub?.();
        this.unsub = undefined;
        if (this.mq && this._onViewport) {
          if (this.mq.removeEventListener) {
            this.mq.removeEventListener("change", this._onViewport);
          } else if (this.mq.removeListener) {
            this.mq.removeListener(this._onViewport);
          }
        }
        this.mq = undefined;
        this._onViewport = undefined;
      }

      connectedCallback() {
        if (this._ready) return;

        try {
          this.data = JSON.parse(this.querySelector(".usm__data").textContent);
        } catch (e) {
          console.error("[upgrade-set-selector] failed to parse data island", e);
          this.hidden = true;
          return;
        }

        this.saveLabel = this.dataset.saveLabel || "";
        this.sectionId = this.dataset.sectionId;
        this.form = document.getElementById(this.dataset.formId);
        this.idInput = this.form
          ? this.form.querySelector('input[name="id"]')
          : null;

        this.optionsLabel = this.dataset.optionsLabel || "Upgrade options";
        this.viewSetLabel = this.dataset.viewSetLabel || "";

        this.list = this.querySelector("[data-usm-list]");
        this.rows = [];
        this.selectedKey = null;
        this.hostOptions = (this.data.selectedHostOptions || []).slice();
        this._boundList = false;
        this._boundSubmit = false;

        this.markLinkOnly();
        this.bindSubmit();
        this.bindVariantChange();

        this._ready = true;
        this.applyMode();
        this.watchViewport();
      }

      // Does the host product carry this option name? Matching is by NAME,
      // case-insensitively — the host's own values are what constrain a set.
      hostConstrains(name) {
        var wanted = String(name).toLowerCase();
        return (this.data.hostOptionNames || []).some(function (hostName) {
          return String(hostName).toLowerCase() === wanted;
        });
      }

      // A row is LINK-ONLY when the set carries an option the host does not
      // (dresser [Color] host vs. bedroom set [Color, Size]): the module would
      // have to choose that value for the shopper. Fine for a deep link — the
      // set's own PDP shows the choice and lets them change it — not fine for
      // add-to-cart, so a link-only row is a "View set" link on every viewport
      // and never steers input[name="id"].
      markLinkOnly() {
        var self = this;
        var options = this.data.options || [];
        options.forEach(function (option) {
          option.linkOnly = (option.optionNames || []).some(function (name) {
            return !self.hostConstrains(name);
          });
        });
        // No selectable set → nothing can be carted in place, so the base
        // ("Just this piece") row has no counterpart to sit beside and the
        // module is a plain link list on every viewport.
        this.forceLink = !options.some(function (option) {
          return option.key !== "base" && !option.linkOnly;
        });
      }

      // `change` fires only when the 750px boundary is actually crossed (window
      // resize, device rotation, responsive-design mode) — not on every resize.
      watchViewport() {
        if (this.mq) return;
        this.mq = window.matchMedia(DESKTOP_QUERY);
        var self = this;
        this._onViewport = function () {
          if (self._ready) self.applyMode();
        };
        if (this.mq.addEventListener) {
          this.mq.addEventListener("change", this._onViewport);
        } else if (this.mq.addListener) {
          this.mq.addListener(this._onViewport);
        }
      }

      applyMode() {
        this.mode =
          this.forceLink || window.matchMedia(DESKTOP_QUERY).matches
            ? "link"
            : "inline";
        // Drop any inline selection before rendering so syncRows never steers the
        // form while in link mode.
        if (this.mode === "link") this.selectedKey = null;

        this.renderList();

        if (this.mode === "inline") {
          this.warnForm();
          this.select("base");
          return;
        }

        // Link mode must not leave a set variant id on the host form: UpCart reads
        // input[name=id] directly, so a stale set id here would cart the wrong
        // product while the UI shows plain "View set" links.
        this.releaseForm();
      }

      // Always re-query: product-info / section morphs can replace the input node.
      getIdInput() {
        if (!this.form) {
          this.form = document.getElementById(this.dataset.formId);
        }
        if (!this.form) return null;
        var input = this.form.querySelector('input[name="id"]');
        this.idInput = input;
        return input;
      }

      // UpCart intercepts the ATC click and never fires form `submit`, so steering
      // must happen on selection (and after option sync) — not submit-only.
      steerForm(variant) {
        if (this.mode !== "inline" || !variant) return;
        var input = this.getIdInput();
        if (!input) return;
        input.value = variant.id;
        input.removeAttribute("disabled");
      }

      // Restore the matching host variant on the shared form (link mode, hide, and
      // leaving inline after a set was selected). Must succeed even when the host
      // is sold out: otherwise a steered set id stays on input[name=id] while the
      // UI shows links / nothing, and UpCart would cart the set.
      releaseForm() {
        var input = this.getIdInput();
        if (!input) return;
        var hostVariant = this.resolveVariant(this.optionByKey("base"), true);
        if (!hostVariant) return;
        input.value = hostVariant.id;
        input.removeAttribute("disabled");
      }

      warnForm() {
        if (this._formWarned) return;
        if (!this.form) {
          console.error(
            "[upgrade-set-selector] requires form #" + this.dataset.formId,
          );
          this._formWarned = true;
          return;
        }
        if (!this.idInput) {
          console.error(
            '[upgrade-set-selector] requires input[name="id"] in form #' +
              this.dataset.formId,
          );
          this._formWarned = true;
        }
      }

      renderList() {
        if (!this.list) return;

        var options = this.data.options || [];
        var self = this;
        var html = "";

        if (this.mode === "inline") {
          var radios = "";
          var links = "";
          options.forEach(function (option) {
            if (!option.linkOnly) {
              radios += self.buildRow(option);
              return;
            }
            if (option.url) links += self.buildLinkRow(option);
          });
          // Radios stay inside a radiogroup even when link-only rows share the
          // module. Links sit in a sibling list so they are not radio options.
          html =
            '<div class="usm__radiogroup" role="radiogroup" aria-label="' +
            this.escapeAttr(this.optionsLabel) +
            '">' +
            radios +
            "</div>";
          if (links) html += '<div class="usm__links">' + links + "</div>";
          this.list.removeAttribute("role");
          this.list.removeAttribute("aria-label");
        } else {
          // Links are plain links, not a radiogroup. Only sets are linkable —
          // the base row carries url: null and is already on screen.
          this.list.removeAttribute("role");
          this.list.removeAttribute("aria-label");
          options.forEach(function (option) {
            if (option.url) html += self.buildLinkRow(option);
          });
        }

        this.list.innerHTML = html;
        this.rows = Array.prototype.slice.call(
          this.list.querySelectorAll(".usm__row"),
        );

        this.bindListEvents();
        this.syncRows();
      }

      buildRow(option) {
        return (
          '<button type="button" class="usm__row" role="radio" aria-checked="false" data-key="' +
          this.escapeAttr(option.key) +
          '">' +
          this.buildRowBody(option) +
          "</button>"
        );
      }

      buildLinkRow(option) {
        return (
          '<a class="usm__row usm__row--link" data-key="' +
          this.escapeAttr(option.key) +
          '" href="' +
          this.escapeAttr(option.url) +
          '">' +
          this.buildRowBody(option, true) +
          "</a>"
        );
      }

      // Shared inner markup so media / title / price stay in lockstep across
      // modes. Wrappers stay separate (button vs a). `showViewSet` is the
      // link-row "View set" affordance inside .usm__main. Title and prices are
      // filled in by syncRows, which knows the resolved variant.
      buildRowBody(option, showViewSet) {
        var view = "";
        if (showViewSet) {
          view =
            '<span class="usm__view">' +
            this.escapeHtml(this.viewSetLabel) +
            '<svg class="usm__chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 6 15 12 9 18"></polyline></svg>' +
            "</span>";
        }
        return (
          this.buildMedia(option) +
          '<span class="usm__main">' +
          '<span class="usm__name" data-usm-name>' +
          this.rowTitle(option) +
          "</span>" +
          view +
          "</span>" +
          '<span class="usm__right price price--end">' +
          '<span class="usm__price price-item price-item--sale" data-usm-price></span>' +
          '<s class="usm__compare price-item price-item--regular" data-usm-compare hidden></s>' +
          '<span class="usm__save" data-usm-save hidden></span>' +
          "</span>"
        );
      }

      // The module chose the unconstrained values, so the row has to name them:
      // "Shaker Bedroom Set · Queen". The row's price and href come from that
      // same variant, so a shopper never sees a size they did not get.
      rowTitle(option, variant) {
        var html = this.formatTitle(option.name || "");
        var values = variant ? this.resolvedValues(option, variant) : [];
        if (values.length) {
          html +=
            ' <span class="usm__resolved">&middot; ' +
            this.escapeHtml(values.join(" · ")) +
            "</span>";
        }
        return html;
      }

      // Values of this variant for the options the host does not carry.
      resolvedValues(option, variant) {
        var self = this;
        var values = [];
        (option.optionNames || []).forEach(function (name, i) {
          if (!self.hostConstrains(name) && variant.options[i] != null) {
            values.push(variant.options[i]);
          }
        });
        return values;
      }

      // Keep multi-word titles wrapping at spaces only — not mid-word or at "-".
      formatTitle(name) {
        return this.escapeHtml(name).replace(/-/g, "&#8209;");
      }

      buildMedia(option) {
        if (!option.image) return "";
        return (
          '<span class="usm__media">' +
          '<img src="' +
          this.escapeAttr(option.image) +
          '" alt="' +
          this.escapeAttr(option.imageAlt || "") +
          '" loading="lazy" width="96" height="96">' +
          "</span>"
        );
      }

      bindListEvents() {
        if (!this.list || this._boundList) return;
        var self = this;
        this.list.addEventListener("click", function (e) {
          var row = e.target.closest(".usm__row");
          if (!row || !self.list.contains(row)) return;
          if (row.classList.contains("is-unavailable")) {
            e.preventDefault();
            return;
          }
          if (self.mode !== "inline") return;
          self.select(row.dataset.key);
        });
        this.list.addEventListener("keydown", function (e) {
          if (self.mode !== "inline") return;
          var row = e.target.closest(".usm__row");
          if (!row || !self.list.contains(row)) return;
          if (!self.isSelectableRow(row)) return;
          if (e.key === "ArrowDown" || e.key === "ArrowRight") {
            e.preventDefault();
            self.moveFocus(row, 1);
          } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
            e.preventDefault();
            self.moveFocus(row, -1);
          }
        });
        this._boundList = true;
      }

      bindSubmit() {
        if (!this.form || this._boundSubmit) return;
        var self = this;
        // Belt-and-suspenders for themes/apps that still submit the form (Dawn).
        // UpCart never reaches here — see steerForm().
        this.form.addEventListener(
          "submit",
          function (e) {
            if (self.mode !== "inline") return;
            var option = self.optionByKey(self.selectedKey);
            var variant = self.resolveVariant(option);
            // Selected set may have gone unavailable after an option change;
            // fall back to the host (same as syncRows) rather than silently
            // blocking.
            if (!variant && self.selectedKey !== "base") {
              var hostVariant = self.resolveVariant(self.optionByKey("base"));
              if (hostVariant) {
                self.select("base");
                variant = hostVariant;
              }
            }
            if (!variant) {
              e.preventDefault();
              console.error(
                "[upgrade-set-selector] submit blocked: no available variant",
                {
                  selectedKey: self.selectedKey,
                  formId: self.dataset.formId,
                  hostOptions: self.hostOptions,
                },
              );
              return;
            }
            if (!self.getIdInput()) {
              e.preventDefault();
              console.error(
                "[upgrade-set-selector] cannot steer add-to-cart: variant id input missing",
              );
              return;
            }
            self.steerForm(variant);
          },
          true,
        );
        this._boundSubmit = true;
      }

      moveFocus(row, dir) {
        var i = this.rows.indexOf(row);
        if (i < 0 || !this.rows.length) return;
        var nextIdx = (i + dir + this.rows.length) % this.rows.length;
        var attempts = 0;
        while (attempts < this.rows.length) {
          var next = this.rows[nextIdx];
          if (this.isSelectableRow(next)) {
            next.focus();
            this.select(next.dataset.key);
            return;
          }
          nextIdx = (nextIdx + dir + this.rows.length) % this.rows.length;
          attempts++;
        }
      }

      bindVariantChange() {
        var self = this;
        var attach = function () {
          if (
            typeof subscribe !== "function" ||
            typeof PUB_SUB_EVENTS === "undefined"
          ) {
            return false;
          }
          self.unsub = subscribe(
            PUB_SUB_EVENTS.variantChange,
            function (event) {
              if (
                !event ||
                !event.data ||
                event.data.sectionId !== self.sectionId
              ) {
                return;
              }
              if (event.data.variant && event.data.variant.options) {
                self.hostOptions = event.data.variant.options.slice();
              }
              self.syncRows();
            },
          );
          return true;
        };

        if (!attach()) {
          if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", attach, {
              once: true,
            });
          }
        }
      }

      // Selectable rows are the radios: link rows (always anchors) are never
      // selected — they must not leave a set variant id on the host form — and
      // unmatched / sold-out rows are already refused.
      isSelectableRow(row) {
        return (
          row.tagName !== "A" && !row.classList.contains("is-unavailable")
        );
      }

      rowByKey(key) {
        return this.rows.filter(function (row) {
          return row.dataset.key === key;
        })[0];
      }

      select(key) {
        var target = this.rowByKey(key);
        if (!target || !this.isSelectableRow(target)) return;
        this.selectedKey = key;
        this.rows.forEach(function (row) {
          var on = row.dataset.key === key;
          row.classList.toggle("is-selected", on);
          if (row.tagName !== "A") {
            row.setAttribute("aria-checked", on ? "true" : "false");
          }
        });
        this.syncRows();
      }

      optionByKey(key) {
        return (this.data.options || []).filter(function (o) {
          return o.key === key;
        })[0];
      }

      // Match by shared option NAME (case-insensitive). Prefer available among
      // matches. Fail closed: no match → null (never fall back to a different
      // option value). Sold-out matches are null unless allowUnavailable — that
      // path is for releasing the form, where the host id must come back even
      // when Dawn will then disable ATC.
      resolveVariant(option, allowUnavailable) {
        if (!option || !option.variants || !option.variants.length) return null;

        var hostMap = {};
        var self = this;
        (this.data.hostOptionNames || []).forEach(function (name, i) {
          if (self.hostOptions[i] != null) {
            hostMap[String(name).toLowerCase()] = String(
              self.hostOptions[i],
            ).toLowerCase();
          }
        });

        var optionNames = (option.optionNames || []).map(function (name) {
          return String(name).toLowerCase();
        });
        // Positions the host cannot constrain — the module chooses these itself.
        var unconstrained = [];
        optionNames.forEach(function (name, i) {
          if (hostMap[name] == null) unconstrained.push(i);
        });

        function matches(variant) {
          for (var i = 0; i < optionNames.length; i++) {
            if (hostMap[optionNames[i]] == null) continue;
            if (
              String(variant.options[i]).toLowerCase() !==
              hostMap[optionNames[i]]
            ) {
              return false;
            }
          }
          return true;
        }

        var matchesList = option.variants.filter(matches);
        if (!matchesList.length) return null;

        var available = matchesList.filter(function (v) {
          return v.available;
        });
        // Fully constrained hosts (bed → bedroom set) never reach this branch.
        if (unconstrained.length) {
          var preferred = this.preferVariant(
            available,
            optionNames,
            unconstrained,
          );
          if (preferred) return preferred;
        }
        if (available[0]) return available[0];
        return allowUnavailable ? matchesList[0] : null;
      }

      // Queen, then King, on an unconstrained `size`. Everything else — other
      // option names, sold-out Queen and King — falls through to the caller's
      // first-available behaviour.
      preferVariant(available, optionNames, unconstrained) {
        var sizeIdx = -1;
        for (var i = 0; i < unconstrained.length; i++) {
          if (optionNames[unconstrained[i]] === SIZE_OPTION) {
            sizeIdx = unconstrained[i];
            break;
          }
        }
        if (sizeIdx === -1) return null;

        for (var p = 0; p < SIZE_PREFERENCE.length; p++) {
          var pattern = SIZE_PREFERENCE[p];
          var match = available.filter(function (v) {
            return pattern.test(String(v.options[sizeIdx]));
          })[0];
          if (match) return match;
        }
        return null;
      }

      syncRows() {
        var self = this;
        this.rows.forEach(function (row) {
          var key = row.dataset.key;
          var option = self.optionByKey(key);
          var variant = self.resolveVariant(option);

          if (!variant) {
            row.classList.add("is-unavailable");
            row.hidden = true;
            row.setAttribute("aria-disabled", "true");
            row.tabIndex = -1;
            // Fail closed: no matching set variant → not a link at all.
            if (row.tagName === "A") row.removeAttribute("href");
            return;
          }

          row.classList.remove("is-unavailable");
          row.hidden = false;
          row.removeAttribute("aria-disabled");

          if (row.tagName === "A") {
            // Links keep default tab order (the roving tabindex below is inline-only).
            row.removeAttribute("tabindex");
            if (option && option.url) {
              // Deep-link with the resolved variant already selected on the set PDP.
              row.setAttribute("href", option.url + "?variant=" + variant.id);
            }
          }

          var nameEl = row.querySelector("[data-usm-name]");
          var priceEl = row.querySelector("[data-usm-price]");
          var compareEl = row.querySelector("[data-usm-compare]");
          var saveEl = row.querySelector("[data-usm-save]");

          if (nameEl) {
            nameEl.innerHTML = self.rowTitle(option, variant);
          }
          if (priceEl) {
            priceEl.textContent = variant.priceMoney || "";
          }
          if (compareEl) {
            if (variant.compareMoney) {
              compareEl.textContent = variant.compareMoney;
              compareEl.hidden = false;
            } else {
              compareEl.textContent = "";
              compareEl.hidden = true;
            }
          }
          if (saveEl) {
            if (variant.saveMoney) {
              saveEl.textContent = self.saveLabel + " " + variant.saveMoney;
              saveEl.hidden = false;
            } else {
              saveEl.textContent = "";
              saveEl.hidden = true;
            }
          }
        });

        // Hide unless at least one SET is purchasable in this option combination.
        // The base row does not keep the module alive (a bundle heading above only
        // the host product offers no bundle). A sold-out host with an in-stock set
        // still shows — the set is the remaining offer. Hide is "no purchasable
        // set", not "host unavailable".
        this.hidden = !this.rows.some(function (row) {
          return !row.hidden && row.dataset.key !== "base";
        });
        // select("base") no-ops when the base row is itself unavailable, so hiding
        // cannot rely on that path to clear a steered set id.
        if (this.hidden) this.releaseForm();

        if (this.mode === "inline" && this.selectedKey) {
          var selectedRow = this.rowByKey(this.selectedKey);
          if (!selectedRow || !this.isSelectableRow(selectedRow)) {
            this.select("base");
            return;
          }

          // product-info resets input[name=id] to the host product on option
          // change; re-apply the selected set/host variant so UpCart/Dawn ATC
          // stay in sync.
          var selectedVariant = this.resolveVariant(
            this.optionByKey(this.selectedKey),
          );
          if (selectedVariant) this.steerForm(selectedVariant);
        }

        // APG radiogroup: one tab stop — selected available radio (else first available).
        // Inline only; link rows are ordinary links in normal tab order.
        if (this.mode !== "inline") return;

        var tabStopKey = null;
        var selected = this.rowByKey(this.selectedKey);
        if (selected && this.isSelectableRow(selected)) {
          tabStopKey = this.selectedKey;
        } else {
          for (var u = 0; u < this.rows.length; u++) {
            if (this.isSelectableRow(this.rows[u])) {
              tabStopKey = this.rows[u].dataset.key;
              break;
            }
          }
        }
        this.rows.forEach(function (row) {
          if (row.tagName === "A") return;
          if (row.classList.contains("is-unavailable")) {
            row.tabIndex = -1;
            return;
          }
          row.tabIndex = row.dataset.key === tabStopKey ? 0 : -1;
        });
      }

      escapeHtml(str) {
        return String(str)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;");
      }

      escapeAttr(str) {
        return this.escapeHtml(str).replace(/'/g, "&#39;");
      }
    },
  );
})();
